import { useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import ChevronLeftIcon from "@atlaskit/icon/core/chevron-left";
import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components";
import { PullQuote } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";

export interface CustomerQuote {
    companyName: string;
    companyLogo: Logo[];
    quote: string;
    advocateName: string;
    advocateJobTitle: string;
}

export interface Logo {
    src: string;
    alt: string;
    href: string;
}

export interface CarouselProps {
    customerQuotes: CustomerQuote[];
}

export interface ChevronProps {
    type: "left" | "right";
}

const messages = {
    previousSlide: {
        id: "customer-quote-carousel.previous-slide",
        defaultMessage: "Previous slide"
    },
    nextSlide: {
        id: "customer-quote-carousel.previous-slide",
        defaultMessage: "Previous slide"
    }
};

export const CustomerQuoteCarousel = ({ customerQuotes }: CarouselProps) => {
    const [activeQuoteIndex, setactiveQuoteIndex] = useState(0);

    const prevSlide = () => {
        if (activeQuoteIndex > 0) setactiveQuoteIndex(activeQuoteIndex - 1);
    };
    const nextSlide = () => {
        if (activeQuoteIndex < customerQuotes.length - 1)
            setactiveQuoteIndex(activeQuoteIndex + 1);
    };

    const intl = useIntl();

    return (
        <div css={gridCss}>
            <div
                aria-hidden={true}
                css={[overlapGridItemCss, hiddenCarouselContainerCss]}
            >
                <div css={[gridCss, hiddenQuoteCss]}>
                    {customerQuotes.map((customerQuote, index) => (
                        <div
                            key={index}
                            css={[
                                overlapGridItemCss,
                                visibilityHiddenCss,
                                overrideQuotePadding
                            ]}
                        >
                            <PullQuote
                                quote={customerQuote.quote}
                                authorName={customerQuote.advocateName}
                                authorDetails={customerQuote.advocateJobTitle}
                            />
                        </div>
                    ))}
                </div>
            </div>
            <div css={[carouselContainerCss, lightTextCss, overlapGridItemCss]}>
                <div css={logoAndButtonContainerCss}>
                    <div css={gridCss}>
                        {customerQuotes.map(({ companyLogo }, index) => (
                            <div
                                key={index}
                                css={css({
                                    gridArea: "1/1",
                                    visibility:
                                        index === activeQuoteIndex
                                            ? "visible"
                                            : "hidden",
                                    width: "140px",
                                    height: "40px"
                                })}
                                aria-hidden={index !== activeQuoteIndex}
                            >
                                <Image
                                    // @ts-ignore
                                    src={companyLogo[0].src}
                                    // @ts-ignore
                                    alt={companyLogo[0].alt}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        objectPosition: "left"
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                    <div css={buttonsContainerCss}>
                        <button
                            onClick={prevSlide}
                            disabled={activeQuoteIndex === 0}
                            css={buttonCss}
                            aria-label={intl.formatMessage(
                                messages.previousSlide
                            )}
                        >
                            <div css={chevronCirlceCss}>
                                <ChevronLeftIcon
                                    label={intl.formatMessage(
                                        messages.previousSlide
                                    )}
                                />
                            </div>
                        </button>
                        <p css={[lightTextCss, slideNumberCss]}>{`${
                            activeQuoteIndex + 1
                        }/${customerQuotes.length}`}</p>
                        <button
                            onClick={nextSlide}
                            disabled={
                                activeQuoteIndex === customerQuotes.length - 1
                            }
                            css={buttonCss}
                            aria-label={intl.formatMessage(messages.nextSlide)}
                        >
                            <div css={chevronCirlceCss}>
                                <ChevronRightIcon
                                    label={intl.formatMessage(
                                        messages.nextSlide
                                    )}
                                />
                            </div>
                        </button>
                    </div>
                </div>
                <div css={[gridCss, overrideQuotePadding]}>
                    <PullQuote
                        // @ts-ignore
                        quote={customerQuotes[activeQuoteIndex].quote}
                        authorName={
                            // @ts-ignore
                            customerQuotes[activeQuoteIndex].advocateName
                        }
                        authorDetails={
                            // @ts-ignore
                            customerQuotes[activeQuoteIndex].advocateJobTitle
                        }
                    />
                </div>
            </div>
        </div>
    );
};

const lightTextCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h2, p, span": {
        color: `${wacToken("color.neutral.dark1000")} !important`
    }
});

const carouselContainerCss = css({
    borderRadius: wacToken("corner.radius.card"),
    backgroundColor: wacToken("color.neutral.dark100"),
    padding: token("space.400"),
    height: "fit-content",
    display: "flex",
    flexDirection: "column",
    gap: token("space.300"),

    [media.below.sm]: {
        padding: token("space.300")
    }
});

const logoAndButtonContainerCss = css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
});

const gridCss = css({
    display: "grid",
    height: "fit-content"
});

const overlapGridItemCss = css({
    gridArea: "1/1"
});

const buttonsContainerCss = css({
    display: "flex",
    alignItems: "center",
    gap: token("space.150")
});

const slideNumberCss = css({
    width: "21px",
    marginBlock: 0
});

const buttonCss = css({
    height: "44px",
    width: "44px",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "100%",

    ["&:hover"]: {
        cursor: "pointer"
    },

    ["&:disabled"]: {
        ["&:hover"]: {
            cursor: "default"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        div: {
            cursor: "default",
            backgroundColor: wacToken("color.neutral.light1000")
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        path: {
            fill: wacToken("color.neutral.light800")
        }
    }
});

const chevronCirlceCss = css({
    "height": "32px",
    "width": "32px",
    "backgroundColor": wacToken("color.neutral.dark400"),
    "border": "none",
    "borderRadius": "100%",
    "display": "flex",
    "justifyContent": "center",
    "alignItems": "center",

    "&:hover": {
        cursor: "pointer",
        backgroundColor: wacToken("color.neutral.dark500")
    }
});

const hiddenCarouselContainerCss = css({
    padding: token("space.400"),

    [media.below.sm]: {
        padding: token("space.300")
    }
});

const hiddenQuoteCss = css({
    paddingBlockStart: "68px"
});

const visibilityHiddenCss = css({
    visibility: "hidden"
});

const overrideQuotePadding = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "> div": {
        paddingBlock: 0
    }
});
